import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Tooltip,
    Grid,
    Card,
    CardContent,
    Avatar,
    Fade,
    Container,
    IconButton,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    Divider,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CheckCircle,
    Send,
    Chat,
    Star,
    Business,
    Public,
    FilterList,
    Clear,
    Search,
    Download,
    Web as WebIcon,
    Close as CloseIcon,
    FileDownload as FileDownloadIcon,
    Description as DescriptionIcon,
    TableChart as TableChartIcon,
    Code as CodeIcon,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { Application } from '../types';

const Applications: React.FC = observer(() => {
    const { applicationStore, tildaStore, settingsStore } = useStore();
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [formFilter, setFormFilter] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openExportDialog, setOpenExportDialog] = useState(false);

    useEffect(() => {
        loadApplications();
        // Загружаем формы Tilda для фильтрации
        tildaStore.fetchTildaPages();
        // Загружаем настройки для получения URL CRM
        settingsStore.fetchSettings();
    }, [page, rowsPerPage, statusFilter, formFilter, startDate, endDate]);

    const loadApplications = () => {
        applicationStore.fetchApplications(
            page + 1,
            rowsPerPage,
            statusFilter || undefined,
            undefined, // type parameter removed
            startDate || undefined,
            endDate || undefined,
            formFilter || undefined // added form_id parameter
        );
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCrmClick = (application: Application) => {
        if (application.sent_to_crm && application.lead_id_crm && settingsStore.settings?.crm.bpm_url) {
            // Формируем URL для перехода в CRM
            const crmUrl = `${settingsStore.settings.crm.bpm_url}/0/Nui/ViewModule.aspx#CardModuleV2/LeadPageV2/edit/${application.lead_id_crm}/`;
            window.open(crmUrl, '_blank');
        }
    };

    const clearFilters = () => {
        setStatusFilter('');
        setFormFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setPage(0);
    };

    const getStatusColor = (status: string) => {
        const colors = {
            'completed': 'success',
            'processing': 'info',
            'pending': 'warning',
            'failed': 'error',
        };
        return colors[status as keyof typeof colors] || 'default';
    };

    const getStatusText = (status: string) => {
        const texts = {
            'completed': 'Завершена',
            'processing': 'Обрабатывается',
            'pending': 'Ожидает',
            'failed': 'Ошибка',
        };
        return texts[status as keyof typeof texts] || status;
    };

    const getFormIcon = (formId: string) => {
        // Можно добавить кастомные иконки для разных форм
        return <WebIcon color="primary" fontSize="small" />;
    };

    const getFormName = (formId: string) => {
        const form = tildaStore.getTildaPageById(formId);
        return form?.name || formId;
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'vip':
                return <Star color="warning" fontSize="small" />;
            case 'corporate':
                return <Business color="primary" fontSize="small" />;
            default:
                return <Public color="action" fontSize="small" />;
        }
    };

    const formatDate = (dateString: string) => {
        return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
    };

    const filteredApplications = applicationStore.applications.filter(app =>
        (searchTerm === '' ||
            app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
            app.email.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (formFilter === '' || app.type === formFilter)
    );

    const activeFiltersCount = [statusFilter, formFilter, startDate, endDate, searchTerm]
        .filter(Boolean).length;

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h3" gutterBottom fontWeight="bold" color="primary">
                            Заявки
                        </Typography>
                        <Typography variant="h6" color="textSecondary">
                            Управление и отслеживание заявок
                        </Typography>
                    </Box>
                    <Box display="flex" gap={2}>
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={() => setOpenExportDialog(true)}
                        >
                            Экспорт
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadApplications}
                            disabled={applicationStore.loading}
                        >
                            Обновить
                        </Button>
                    </Box>
                </Box>

                {applicationStore.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {applicationStore.error}
                    </Alert>
                )}

                {/* Быстрая статистика */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ py: 2 }}>
                                <Box display="flex" alignItems="center">
                                    <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                        <Public />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6">{applicationStore.total}</Typography>
                                        <Typography variant="body2" color="textSecondary">Всего заявок</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Фильтры */}
                <Fade in timeout={300}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Box display="flex" alignItems="center">
                                <FilterList sx={{ mr: 1 }} />
                                <Typography variant="h6">Фильтры</Typography>
                                {activeFiltersCount > 0 && (
                                    <Badge badgeContent={activeFiltersCount} color="primary" sx={{ ml: 2 }} />
                                )}
                            </Box>
                            <Button
                                variant="text"
                                startIcon={<Clear />}
                                onClick={clearFilters}
                                disabled={activeFiltersCount === 0}
                            >
                                Очистить все
                            </Button>
                        </Box>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Поиск..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Статус</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label="Статус"
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            setPage(0);
                                        }}
                                    >
                                        <MenuItem value="">Все</MenuItem>
                                        <MenuItem value="pending">Ожидает</MenuItem>
                                        <MenuItem value="processing">Обрабатывается</MenuItem>
                                        <MenuItem value="completed">Завершена</MenuItem>
                                        <MenuItem value="failed">Ошибка</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Форма</InputLabel>
                                    <Select
                                        value={formFilter}
                                        label="Форма"
                                        onChange={(e) => {
                                            setFormFilter(e.target.value);
                                            setPage(0);
                                        }}
                                    >
                                        <MenuItem value="">Все</MenuItem>
                                        {tildaStore.tildaPages.map((form) => (
                                            <MenuItem key={form.id} value={form.id}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <WebIcon fontSize="small" color="action" />
                                                    {form.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Дата от"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Дата до"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2}>
                                <Button
                                    variant="contained"
                                    onClick={loadApplications}
                                    disabled={applicationStore.loading}
                                    fullWidth
                                >
                                    Применить
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Fade>

                {/* Таблица заявок */}
                <Fade in timeout={600}>
                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Клиент</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Компания</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Контакты</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Тариф</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Форма</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Обработка</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Дата</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {applicationStore.loading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                                <CircularProgress size={40} />
                                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                                    Загрузка заявок...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredApplications.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                                <Public sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                                                <Typography variant="h6" color="textSecondary">
                                                    Заявки не найдены
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    Попробуйте изменить параметры фильтрации
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredApplications.map((application) => (
                                            <TableRow
                                                key={application.id}
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                        transform: 'scale(1.001)',
                                                    },
                                                    transition: 'all 0.2s ease-in-out'
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        #{application.id}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center">
                                                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                                                            {application.full_name.charAt(0)}
                                                        </Avatar>
                                                        <Typography variant="body2" fontWeight="500">
                                                            {application.full_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight="500">
                                                        {application.company}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {application.company_size} сотр.
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{application.email}</Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {application.phone}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={application.tariff}
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center">
                                                        {getFormIcon(application.type)}
                                                        <Chip
                                                            label={getFormName(application.type)}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={getStatusText(application.status)}
                                                        color={getStatusColor(application.status) as any}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1}>
                                                        <Tooltip title={application.email_verified ? 'Email подтвержден' : 'Email не подтвержден'}>
                                                            <IconButton size="small">
                                                                <CheckCircle
                                                                    color={application.email_verified ? 'success' : 'disabled'}
                                                                    fontSize="small"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={
                                                            application.sent_to_crm
                                                                ? application.lead_id_crm
                                                                    ? `Отправлено в CRM (ID: ${application.lead_id_crm}). Нажмите для перехода`
                                                                    : 'Отправлено в CRM'
                                                                : 'Не отправлено в CRM'
                                                        }>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleCrmClick(application)}
                                                                disabled={!application.sent_to_crm || !application.lead_id_crm}
                                                                sx={{
                                                                    cursor: application.sent_to_crm && application.lead_id_crm ? 'pointer' : 'default',
                                                                    '&:hover': {
                                                                        backgroundColor: application.sent_to_crm && application.lead_id_crm ? 'action.hover' : 'transparent',
                                                                    }
                                                                }}
                                                            >
                                                                <Send
                                                                    color={application.sent_to_crm ? 'success' : 'disabled'}
                                                                    fontSize="small"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={application.sent_to_mattermost ? 'Отправлено в Mattermost' : 'Не отправлено в Mattermost'}>
                                                            <IconButton size="small">
                                                                <Chat
                                                                    color={application.sent_to_mattermost ? 'success' : 'disabled'}
                                                                    fontSize="small"
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(application.created_at)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={applicationStore.total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage="Строк на странице:"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
                            }
                        />
                    </Paper>
                </Fade>
            </Box>

            {/* Диалог экспорта */}
            <ExportDialog
                open={openExportDialog}
                onClose={() => setOpenExportDialog(false)}
                applications={filteredApplications}
                getFormName={getFormName}
            />
        </Container>
    );
});

// Компонент диалога экспорта
type ExportFormat = 'csv' | 'xlsx' | 'json';

interface ExportField {
    key: keyof Application;
    label: string;
    enabled: boolean;
}

interface ExportDialogProps {
    open: boolean;
    onClose: () => void;
    applications: Application[];
    getFormName: (formId: string) => string;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
                                                       open,
                                                       onClose,
                                                       applications,
                                                       getFormName,
                                                   }) => {
    const [exportFormat, setExportFormat] = useState<ExportFormat>('xlsx');
    const [exporting, setExporting] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [exportFields, setExportFields] = useState<ExportField[]>([
        { key: 'id', label: 'ID', enabled: true },
        { key: 'full_name', label: 'ФИО', enabled: true },
        { key: 'company', label: 'Компания', enabled: true },
        { key: 'company_size', label: 'Размер компании', enabled: true },
        { key: 'email', label: 'Email', enabled: true },
        { key: 'phone', label: 'Телефон', enabled: true },
        { key: 'tariff', label: 'Тариф', enabled: true },
        { key: 'form_id', label: 'Форма', enabled: true },
        { key: 'status', label: 'Статус', enabled: true },
        { key: 'email_verified', label: 'Email подтвержден', enabled: true },
        { key: 'sent_to_crm', label: 'Отправлено в CRM', enabled: true },
        { key: 'sent_to_mattermost', label: 'Отправлено в Mattermost', enabled: true },
        { key: 'created_at', label: 'Дата создания', enabled: true },
        { key: 'comment', label: 'Комментарий', enabled: false },
        { key: 'type_client', label: 'Тип клиента', enabled: false },
        { key: 'vacancy_name', label: 'Вакансия', enabled: false },
        { key: 'referer', label: 'Источник', enabled: false },
        { key: 'utm_source', label: 'UTM Source', enabled: false },
        { key: 'utm_medium', label: 'UTM Medium', enabled: false },
        { key: 'utm_campaign', label: 'UTM Campaign', enabled: false },
    ]);

    const handleFieldToggle = (index: number) => {
        const newFields = [...exportFields];
        newFields[index].enabled = !newFields[index].enabled;
        setExportFields(newFields);
    };

    const toggleAllFields = (enabled: boolean) => {
        setExportFields(exportFields.map(field => ({ ...field, enabled })));
    };

    const getEnabledFieldsCount = () => {
        return exportFields.filter(field => field.enabled).length;
    };

    const filterApplicationsByDate = (apps: Application[]) => {
        return apps.filter(app => {
            const appDate = new Date(app.created_at);
            const from = dateFrom ? new Date(dateFrom) : null;
            const to = dateTo ? new Date(dateTo) : null;

            if (from && appDate < from) return false;
            if (to && appDate > new Date(dateTo + 'T23:59:59')) return false;

            return true;
        });
    };

    const formatValue = (value: any, key: keyof Application): string => {
        if (value === null || value === undefined) return '';

        switch (key) {
            case 'created_at':
            case 'updated_at':
                return format(new Date(value), 'dd.MM.yyyy HH:mm', { locale: ru });
            case 'email_verified':
            case 'sent_to_crm':
            case 'sent_to_mattermost':
                return value ? 'Да' : 'Нет';
            case 'form_id':
                return getFormName(value);
            case 'status':
                const statusTexts = {
                    'completed': 'Завершена',
                    'processing': 'Обрабатывается',
                    'pending': 'Ожидает',
                    'failed': 'Ошибка',
                };
                return statusTexts[value as keyof typeof statusTexts] || value;
            default:
                return String(value);
        }
    };

    const exportToCSV = (data: any[]) => {
        const enabledFields = exportFields.filter(field => field.enabled);

        // Создаем заголовки
        const headers = enabledFields.map(field => field.label).join(',');

        // Создаем строки данных
        const rows = data.map(app => {
            return enabledFields.map(field => {
                const value = formatValue(app[field.key], field.key);
                // Экранируем значения с запятыми
                return value.includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
            }).join(',');
        });

        // Объединяем все
        const csv = [headers, ...rows].join('\n');

        // Создаем BOM для корректного отображения кириллицы
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

        // Скачиваем файл
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `applications_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
        link.click();
    };

    const exportToXLSX = (data: any[]) => {
        const enabledFields = exportFields.filter(field => field.enabled);

        // Подготавливаем данные
        const wsData = data.map(app => {
            const row: any = {};
            enabledFields.forEach(field => {
                row[field.label] = formatValue(app[field.key], field.key);
            });
            return row;
        });

        // Создаем книгу и лист
        const ws = XLSX.utils.json_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Заявки');

        // Настраиваем ширину колонок
        const colWidths = enabledFields.map(field => ({
            wch: Math.max(field.label.length, 15)
        }));
        ws['!cols'] = colWidths;

        // Скачиваем файл
        XLSX.writeFile(wb, `applications_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`);
    };

    const exportToJSON = (data: any[]) => {
        const enabledFields = exportFields.filter(field => field.enabled);

        // Фильтруем только нужные поля
        const filteredData = data.map(app => {
            const obj: any = {};
            enabledFields.forEach(field => {
                obj[field.key] = app[field.key];
            });
            return obj;
        });

        // Создаем JSON
        const json = JSON.stringify(filteredData, null, 2);
        const blob = new Blob([json], { type: 'application/json' });

        // Скачиваем файл
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `applications_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.json`;
        link.click();
    };

    const handleExport = async () => {
        setExporting(true);

        try {
            // Фильтруем заявки по дате
            const filteredApps = filterApplicationsByDate(applications);

            if (filteredApps.length === 0) {
                alert('Нет данных для экспорта с выбранными фильтрами');
                setExporting(false);
                return;
            }

            // Экспортируем в выбранном формате
            switch (exportFormat) {
                case 'csv':
                    exportToCSV(filteredApps);
                    break;
                case 'xlsx':
                    exportToXLSX(filteredApps);
                    break;
                case 'json':
                    exportToJSON(filteredApps);
                    break;
            }

            // Закрываем диалог после успешного экспорта
            setTimeout(() => {
                setExporting(false);
                onClose();
            }, 1000);
        } catch (error) {
            console.error('Export error:', error);
            alert('Ошибка при экспорте данных');
            setExporting(false);
        }
    };

    const getFormatIcon = (format: ExportFormat) => {
        switch (format) {
            case 'csv':
                return <TableChartIcon />;
            case 'xlsx':
                return <DescriptionIcon />;
            case 'json':
                return <CodeIcon />;
        }
    };

    const filteredCount = filterApplicationsByDate(applications).length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Экспорт заявок</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Фильтр по датам */}
                <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Период экспорта
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center">
                        <TextField
                            size="small"
                            label="Дата от"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            size="small"
                            label="Дата до"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                        />
                        <Chip
                            label={`${filteredCount} заявок`}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    {(dateFrom || dateTo) && (
                        <Button
                            size="small"
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                            }}
                            sx={{ mt: 1 }}
                        >
                            Сбросить период
                        </Button>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Выбор формата */}
                <Box mb={3}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                            Формат экспорта
                        </FormLabel>
                        <RadioGroup
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                            row
                        >
                            <FormControlLabel
                                value="xlsx"
                                control={<Radio />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {getFormatIcon('xlsx')}
                                        <span>Excel (.xlsx)</span>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="csv"
                                control={<Radio />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {getFormatIcon('csv')}
                                        <span>CSV (.csv)</span>
                                    </Box>
                                }
                            />
                            <FormControlLabel
                                value="json"
                                control={<Radio />}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {getFormatIcon('json')}
                                        <span>JSON (.json)</span>
                                    </Box>
                                }
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Выбор полей */}
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Поля для экспорта ({getEnabledFieldsCount()} из {exportFields.length})
                        </Typography>
                        <Box>
                            <Button size="small" onClick={() => toggleAllFields(true)}>
                                Выбрать все
                            </Button>
                            <Button size="small" onClick={() => toggleAllFields(false)}>
                                Снять все
                            </Button>
                        </Box>
                    </Box>

                    <FormGroup>
                        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={1}>
                            {exportFields.map((field, index) => (
                                <FormControlLabel
                                    key={field.key}
                                    control={
                                        <Checkbox
                                            checked={field.enabled}
                                            onChange={() => handleFieldToggle(index)}
                                            size="small"
                                        />
                                    }
                                    label={field.label}
                                />
                            ))}
                        </Box>
                    </FormGroup>
                </Box>

                {filteredCount === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Нет заявок для экспорта с выбранными фильтрами
                    </Alert>
                )}

                {getEnabledFieldsCount() === 0 && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        Выберите хотя бы одно поле для экспорта
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={exporting}>
                    Отмена
                </Button>
                <Button
                    variant="contained"
                    onClick={handleExport}
                    disabled={exporting || getEnabledFieldsCount() === 0 || filteredCount === 0}
                    startIcon={exporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                >
                    {exporting ? 'Экспорт...' : `Экспортировать (${filteredCount})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Applications;